import pmProjectService from '@/services/pmProjectService';
import {
  readStoredProjectId,
  resolveDefaultProjectId,
  writeStoredProjectId,
} from '@/utils/userProjectsContext';

export default {
  namespaced: true,

  state: {
    projects: [],
    total: 0,
    currentProject: null,
    selectedProjectId: null,
    loading: false,
    error: null,
    userProjectContextInitialized: false,
  },

  getters: {
    projects: (state) => state.projects,
    total: (state) => state.total,
    currentProject: (state) => state.currentProject,
    selectedProjectId: (state) => state.selectedProjectId,
    selectedProject: (state) => state.projects.find((p) => p.id === state.selectedProjectId) || null,
    activeProjects: (state) => state.projects.filter((p) => p.status === 'active'),
    loading: (state) => state.loading,
    error: (state) => state.error,
    isEmpty: (state) => !state.loading && !state.error && state.projects.length === 0,
    hasUserProjectContext: (state) => Boolean(state.selectedProjectId),
    userProjectContextReady: (state) => state.userProjectContextInitialized && !state.loading,
  },

  mutations: {
    SET_LOADING(state, loading) {
      state.loading = loading;
    },
    SET_ERROR(state, error) {
      state.error = error;
    },
    SET_PROJECTS(state, { items, total }) {
      state.projects = items || [];
      state.total = typeof total === 'number' ? total : state.projects.length;
    },
    SET_CURRENT_PROJECT(state, project) {
      state.currentProject = project;
    },
    SET_SELECTED_PROJECT_ID(state, projectId) {
      state.selectedProjectId = projectId || null;
    },
    SET_USER_PROJECT_CONTEXT_INITIALIZED(state, initialized) {
      state.userProjectContextInitialized = initialized === true;
    },
    ADD_PROJECT(state, project) {
      state.projects.unshift(project);
      state.total += 1;
    },
    UPDATE_PROJECT(state, updatedProject) {
      const index = state.projects.findIndex((p) => p.id === updatedProject.id);
      if (index !== -1) {
        state.projects.splice(index, 1, updatedProject);
      }
    },
    REMOVE_PROJECT(state, projectId) {
      state.projects = state.projects.filter((p) => p.id !== projectId);
      state.total = Math.max(0, state.total - 1);
    },
    RESET(state) {
      state.projects = [];
      state.total = 0;
      state.currentProject = null;
      state.selectedProjectId = null;
      state.loading = false;
      state.error = null;
      state.userProjectContextInitialized = false;
    },
  },

  actions: {
    async fetchProjects({ commit }, options = {}) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      try {
        // CMS wants every status. Prefer scope=all; fall back to active+archived merge
        // when the API build does not accept `all` yet.
        let items = [];
        let total = 0;
        if (options.scope === 'all') {
          try {
            const data = await pmProjectService.listProjects({ scope: 'all' });
            items = data?.items || [];
            total = data?.total ?? items.length;
          } catch (error) {
            const message = String(error?.response?.data?.message || error?.message || '');
            const scopeRejected = /scope must be one of/i.test(message)
              || (Array.isArray(error?.response?.data?.message)
                && error.response.data.message.some((item) => /scope must be one of/i.test(String(item))));
            if (!scopeRejected) throw error;
            const [active, archived] = await Promise.all([
              pmProjectService.listProjects({ scope: 'active' }),
              pmProjectService.listProjects({ scope: 'archived' }),
            ]);
            const byId = new Map();
            [...(active?.items || []), ...(archived?.items || [])].forEach((project) => {
              byId.set(String(project.id), project);
            });
            items = [...byId.values()];
            total = items.length;
          }
        } else {
          const data = await pmProjectService.listProjects({
            scope: options.scope,
          });
          items = data?.items || [];
          total = data?.total ?? items.length;
        }
        commit('SET_PROJECTS', { items, total });
      } catch (error) {
        const message = error.response?.data?.message || error.message || 'Failed to load projects';
        commit('SET_ERROR', Array.isArray(message) ? message.join(', ') : message);
        commit('SET_PROJECTS', { items: [], total: 0 });
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    selectProject({ commit }, projectId) {
      commit('SET_SELECTED_PROJECT_ID', projectId);
    },

    async changeUserProjectContext({ commit, dispatch }, projectId) {
      const normalizedId = projectId ? String(projectId) : null;
      commit('SET_SELECTED_PROJECT_ID', normalizedId);
      writeStoredProjectId(normalizedId);
      await dispatch('pmCommitment/setProjectContext', normalizedId, { root: true });
      await dispatch('pmConstraint/setProjectContext', normalizedId, { root: true });
      await dispatch('pmDecision/setProjectContext', normalizedId, { root: true });
      await dispatch('pmMilestone/setProjectContext', normalizedId, { root: true });
      return normalizedId;
    },

    async initializeUserProjectContext({ commit, dispatch, getters }) {
      try {
        await dispatch('fetchProjects');
        const activeProjects = getters.activeProjects || [];
        const defaultProjectId = resolveDefaultProjectId(
          activeProjects,
          readStoredProjectId(),
        );
        await dispatch('changeUserProjectContext', defaultProjectId);
        return defaultProjectId;
      } finally {
        commit('SET_USER_PROJECT_CONTEXT_INITIALIZED', true);
      }
    },

    async fetchProject({ commit }, id) {
      commit('SET_ERROR', null);
      try {
        const project = await pmProjectService.getProject(id);
        commit('SET_CURRENT_PROJECT', project);
        return project;
      } catch (error) {
        const message = error.response?.data?.message || error.message || 'Failed to load project';
        commit('SET_ERROR', message);
        throw error;
      }
    },

    async createProject({ commit, dispatch }, payload) {
      commit('SET_ERROR', null);
      try {
        const project = await pmProjectService.createProject(payload);
        commit('ADD_PROJECT', project);
        return project;
      } catch (error) {
        const message = error.response?.data?.message || error.message || 'Failed to create project';
        commit('SET_ERROR', message);
        throw error;
      }
    },

    async updateProject({ commit, state }, { id, payload }) {
      commit('SET_ERROR', null);
      try {
        const project = await pmProjectService.updateProject(id, payload);
        const isTerminal = project.status === 'completed' || project.status === 'archived';
        if (isTerminal) {
          commit('REMOVE_PROJECT', id);
          if (state.selectedProjectId === id) {
            commit('SET_SELECTED_PROJECT_ID', null);
          }
        } else {
          commit('UPDATE_PROJECT', project);
        }
        return project;
      } catch (error) {
        const message = error.response?.data?.message || error.message || 'Failed to update project';
        commit('SET_ERROR', message);
        throw error;
      }
    },

    async deleteProject({ commit, state }, id) {
      commit('SET_ERROR', null);
      try {
        await pmProjectService.deleteProject(id);
        commit('REMOVE_PROJECT', id);
        if (state.selectedProjectId === id) {
          commit('SET_SELECTED_PROJECT_ID', null);
        }
      } catch (error) {
        const message = error.response?.data?.message || error.message || 'Failed to archive project';
        commit('SET_ERROR', message);
        throw error;
      }
    },
  },
};
